import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../src/contexts/AuthContext';
import { supabase } from '../../src/lib/supabase';
import { derivarStatusBoleto } from '../../src/lib/statusBoleto';
import {
    normalizeMesReferencia,
    economiaDaFatura,
    brl,
    dataBr,
    valorCobradoFatura,
    linkBoletoFatura,
    partesBoletoUnico,
} from './format';
import PagamentoModal, { FaturaPagamento, BoletoQuitacao } from './PagamentoModal';
import {
    FileText,
    Receipt,
    Headset,
    Loader2,
    ArrowLeft,
    ExternalLink,
    AlertCircle,
    Zap,
    CalendarDays,
    Leaf,
    Wallet,
    ChevronDown,
    Eye,
    Info
} from 'lucide-react';
import PortalConsumidorHeader from './PortalConsumidorHeader';
import { useMarcaPortal, useTituloPortal } from './marca/MarcaPortalContext';

// Portal do Consumidor — Minhas Faturas (rota /portal-consumidor/faturas).
// Lista as faturas da Equatorial já ingeridas pelo VigorHub (tabela faturas) e os
// boletos Vigor vinculados. O filtro por CPF NÃO é feito aqui: a RLS restritiva
// (migration 20260709160000_portal_consumidor_rls) garante que um consumidor só
// recebe as linhas cujo cliente_cpf_cnpj/UC batem com o cpf_cnpj do profile dele —
// a query é um select simples e o servidor recorta o que ele pode ver.
// A economia exibida é a oficial persistida em faturas.economia (ver format.ts).

interface FaturaPortal extends FaturaPagamento {
    id: number;
    consumo_ativo_kwh: number | null;
    status_boleto_vigor: string | null;
    status_fatura: string | null;
    tipo_pdf_fatura: string | null;
    economia?: number | null;
    custo_s_vigor?: number | null;
    total_com_vigor?: number | null;
    equatorial_quitada_pelo_cliente?: boolean | null;
    // "Entenda sua fatura": leitura_* são DATAS de leitura; icms/pis/cofins são alíquotas (%)
    leitura_anterior?: string | null;
    leitura_atual?: string | null;
    proxima_leitura?: string | null;
    icms?: number | null;
    pis?: number | null;
    cofins?: number | null;
    ilum_publica?: number | null;
    energia_injetada_gd1_kwh?: number | null;
    energia_injetada_gd2_kwh?: number | null;
    valor_adc_bandeira?: number | null;
    created_at: string;
}

interface BoletoPortal extends BoletoQuitacao {
    id: number;
    fatura_id: number;
    valor_total: number | null;
    valor_com_desconto: number | null;
    data_vencimento: string | null;
    status: string | null;
    pdf_boleto: unknown;
    mes_referencia: string | null;
}

// Status DERIVADO (src/lib/statusBoleto) em que existe boleto para o cliente pagar.
// Lia-se `status_boleto_vigor` cru, e ciclo sem compensação — fatura de "pular
// cálculo", valor 0 — caía em "Em aberto": o cliente via uma cobrança da Vigor de
// R$ 0,00 pendente no portal, sem boleto nenhum por trás.
const STATUS_PENDENTES = ['Em aberto', 'Vencido'];

const pct = (v: number | null | undefined): string =>
    typeof v === 'number' ? `${v.toLocaleString('pt-BR', { maximumFractionDigits: 2 })}%` : '—';

const ItemDetalhe = ({ rotulo, valor, classeValor }: { rotulo: string; valor: string; classeValor?: string }) => (
    <div className="bg-white rounded-xl border border-gray-100 px-4 py-3">
        <p className="text-gray-400 text-[11px] font-medium uppercase">{rotulo}</p>
        <p className={`text-sm font-semibold mt-0.5 ${classeValor || 'text-gray-800'}`}>{valor}</p>
    </div>
);

const statusBadge = (status: string | null) => {
    const s = (status || '').toLowerCase();
    if (s === 'pago' || s === 'quitado') return 'bg-green-100 text-green-700';
    if (s === 'vencido') return 'bg-red-100 text-red-700';
    if (s === 'cancelado' || s === 'sem cobrança' || s === 'sem injeção' || s === 'cobrança agrupada') return 'bg-gray-100 text-gray-500';
    if (s === 'parcelado') return 'bg-secondary/10 text-secondary';
    return 'bg-amber-100 text-amber-700'; // Em aberto / pendente / processada
};

/** Rótulo do status para o cliente — sem jargão interno. */
const statusRotulo = (status: string): string =>
    status === 'Sem cobrança' || status === 'Sem injeção' ? 'Sem cobrança no mês'
        : status === 'Cobrança agrupada' ? 'Cobrada no boleto agrupado'
            : status;

const PortalConsumidorFaturas = () => {
    const { user, profile, loading, signOut } = useAuth();
    const { marca } = useMarcaPortal();
    const EMAIL_SUPORTE = marca.contato.email;

    const [carregando, setCarregando] = useState(true);
    const [erro, setErro] = useState<string | null>(null);
    const [faturas, setFaturas] = useState<FaturaPortal[]>([]);
    const [boletosPorFatura, setBoletosPorFatura] = useState<Map<number, BoletoPortal>>(new Map());
    const [faturaPagamento, setFaturaPagamento] = useState<FaturaPortal | null>(null);
    // Dropdown por fatura (uma expandida por vez): painel "detalhes" (entenda sua
    // fatura) ou "pdf" (visualizador embutido)
    const [expandidaId, setExpandidaId] = useState<number | null>(null);
    const [painel, setPainel] = useState<'detalhes' | 'pdf'>('pdf');
    const [docVisivel, setDocVisivel] = useState<'fatura' | 'boleto'>('fatura');

    const alternarExpansao = (fatura: FaturaPortal, novoPainel: 'detalhes' | 'pdf', temPdfFatura: boolean) => {
        if (expandidaId === fatura.id && painel === novoPainel) {
            setExpandidaId(null);
            return;
        }
        setPainel(novoPainel);
        setDocVisivel(temPdfFatura ? 'fatura' : 'boleto');
        setExpandidaId(fatura.id);
    };

    useTituloPortal('Minhas Faturas');

    useEffect(() => {
        if (!user || profile?.role !== 'consumidor') return;

        const carregar = async () => {
            setCarregando(true);
            setErro(null);
            try {
                const [{ data: fat, error: fatError }, { data: bol, error: bolError }] = await Promise.all([
                    supabase
                        .from('faturas')
                        .select('id, uc_codigo, mes_referencia, data_vencimento, vencimento_boleto, valor_fatura_distribuidora, valor_boleto_vigor, valor_emissao_boleto_unico, equatorial_quitada_pelo_cliente, economia, custo_s_vigor, total_com_vigor, consumo_ativo_kwh, status_boleto_vigor, status_fatura, pdf_fatura, tipo_pdf_fatura, codigo_pix_boleto, url_img_codigo_pix_boleto, linha_digitavel_boleto, url_boleto, url_boleto_combinado, leitura_anterior, leitura_atual, proxima_leitura, icms, pis, cofins, ilum_publica, energia_injetada_gd1_kwh, energia_injetada_gd2_kwh, valor_adc_bandeira, created_at, cobranca_agrupada')
                        .is('excluido_em', null)
                        .order('data_vencimento', { ascending: false, nullsFirst: false }),
                    supabase
                        .from('boletos_vigor')
                        .select('id, fatura_id, valor_total, valor_com_desconto, data_vencimento, status, pdf_boleto, mes_referencia, tem_desconto_quitacao, desc_quitacao_data_pgt_1, desc_quitacao_desconto_percent_1, desc_quitacao_desconto_valor_1, desc_quitacao_a_pagar_1, desc_quitacao_data_pgt_2, desc_quitacao_desconto_percent_2, desc_quitacao_desconto_valor_2, desc_quitacao_a_pagar_2, desc_quitacao_data_pgt_3, desc_quitacao_desconto_percent_3, desc_quitacao_desconto_valor_3, desc_quitacao_a_pagar_3'),
                ]);

                if (fatError) throw fatError;
                if (bolError) throw bolError;

                setFaturas((fat || []) as FaturaPortal[]);
                const map = new Map<number, BoletoPortal>();
                (bol || []).forEach((b: BoletoPortal) => map.set(b.fatura_id, b));
                setBoletosPorFatura(map);
            } catch (err) {
                console.error('Erro ao carregar faturas do portal:', err);
                setErro('Não foi possível carregar suas faturas. Tente novamente em instantes.');
            } finally {
                setCarregando(false);
            }
        };

        carregar();
    }, [user, profile]);

    if (loading) {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center">
                <Loader2 size={32} className="animate-spin text-primary" />
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-50">
            <PortalConsumidorHeader />

            <main className="max-w-5xl mx-auto px-4 sm:px-6 py-10 space-y-6">
                <div className="flex items-center gap-3">
                    <Link
                        to="/portal-consumidor/inicio"
                        className="p-2 rounded-full text-gray-400 hover:text-primary hover:bg-gray-100 transition-colors"
                    >
                        <ArrowLeft size={20} />
                    </Link>
                    <div>
                        <h1 className="text-2xl font-bold text-gray-900">Minhas Faturas</h1>
                        <p className="text-sm text-gray-500">{marca.subtituloFaturas}</p>
                    </div>
                </div>

                {erro && (
                    <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg flex items-start gap-3 text-sm">
                        <AlertCircle size={18} className="mt-0.5 shrink-0" />
                        <span>{erro}</span>
                    </div>
                )}

                {carregando ? (
                    <div className="flex justify-center py-20">
                        <Loader2 size={32} className="animate-spin text-primary" />
                    </div>
                ) : faturas.length === 0 ? (
                    <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-10 text-center space-y-4">
                        <div className="w-16 h-16 mx-auto rounded-2xl bg-primary/10 flex items-center justify-center">
                            <FileText size={30} className="text-primary" />
                        </div>
                        <h2 className="text-lg font-bold text-gray-900">Nenhuma fatura encontrada</h2>
                        <p className="text-sm text-gray-500 max-w-md mx-auto">
                            Ainda não há faturas vinculadas ao seu CPF no nosso sistema. Se você acredita que
                            deveria haver, fale com o nosso atendimento.
                        </p>
                        <a
                            href={`mailto:${EMAIL_SUPORTE}?subject=${encodeURIComponent('Faturas não aparecem no Portal do Consumidor')}`}
                            className="inline-flex items-center gap-2 py-2.5 px-5 rounded-xl text-sm font-bold text-white bg-primary hover:bg-primary/90 transition-all"
                        >
                            <Headset size={16} />
                            Falar com o suporte
                        </a>
                    </div>
                ) : (
                    <div className="space-y-4">
                        {faturas.map((fatura) => {
                            const boleto = boletosPorFatura.get(fatura.id);
                            const economia = economiaDaFatura(fatura);
                            const pdfFaturaUrl = typeof fatura.pdf_fatura === 'string' ? fatura.pdf_fatura : null;
                            const pdfBoletoUrl = boleto && typeof boleto.pdf_boleto === 'string' ? boleto.pdf_boleto : null;
                            const statusReal = derivarStatusBoleto(fatura as any);
                            const pendente = STATUS_PENDENTES.includes(statusReal);
                            // #6117: link do boleto único (PDF no site) antes da página do banco.
                            const boletoUrl = linkBoletoFatura(fatura) || pdfBoletoUrl;
                            const temPagamento = !!(fatura.codigo_pix_boleto || fatura.linha_digitavel_boleto || boletoUrl);
                            const partes = partesBoletoUnico(fatura);
                            const temAlgumPdf = !!(pdfFaturaUrl || boletoUrl);
                            const aberta = expandidaId === fatura.id;
                            const urlVisualizada = docVisivel === 'fatura' ? (pdfFaturaUrl ?? boletoUrl) : (boletoUrl ?? pdfFaturaUrl);

                            return (
                                <div key={fatura.id} className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
                                    <div className="p-6">
                                    <div className="flex flex-wrap items-start justify-between gap-3 mb-4">
                                        <div>
                                            <div className="flex items-center gap-2">
                                                <h2 className="font-bold text-gray-900 text-lg">
                                                    Referência {normalizeMesReferencia(fatura.mes_referencia)}
                                                </h2>
                                                <span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${statusBadge(statusReal)}`}>
                                                    {statusRotulo(statusReal)}
                                                </span>
                                            </div>
                                            <p className="text-xs text-gray-400 mt-1">UC {fatura.uc_codigo}</p>
                                        </div>
                                        <div className="flex flex-wrap gap-2">
                                            {pendente && temPagamento && (
                                                <button
                                                    onClick={() => setFaturaPagamento(fatura)}
                                                    className="inline-flex items-center gap-1.5 py-2 px-5 rounded-xl text-xs font-bold text-white bg-primary hover:bg-primary/90 transition-colors shadow-sm"
                                                >
                                                    <Wallet size={14} />
                                                    Pagar
                                                </button>
                                            )}
                                            {pdfFaturaUrl && (
                                                <a
                                                    href={pdfFaturaUrl}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="inline-flex items-center gap-1.5 py-2 px-4 rounded-xl text-xs font-bold text-primary bg-primary/10 hover:bg-primary/20 transition-colors"
                                                >
                                                    <FileText size={14} />
                                                    Fatura (PDF)
                                                    <ExternalLink size={12} />
                                                </a>
                                            )}
                                            {boletoUrl && (
                                                <a
                                                    href={boletoUrl}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="inline-flex items-center gap-1.5 py-2 px-4 rounded-xl text-xs font-bold text-primary bg-primary/10 hover:bg-primary/20 transition-colors"
                                                >
                                                    <Receipt size={14} />
                                                    Boleto (PDF)
                                                    <ExternalLink size={12} />
                                                </a>
                                            )}
                                        </div>
                                    </div>

                                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 text-sm">
                                        <div className="flex items-start gap-2.5">
                                            <CalendarDays size={17} className="text-primary mt-0.5 shrink-0" />
                                            <div>
                                                <p className="text-gray-400 text-xs font-medium uppercase">Vencimento</p>
                                                <p className="text-gray-800 font-semibold">
                                                    {dataBr(fatura.vencimento_boleto || boleto?.data_vencimento || fatura.data_vencimento)}
                                                </p>
                                            </div>
                                        </div>
                                        <div className="flex items-start gap-2.5">
                                            <Receipt size={17} className="text-primary mt-0.5 shrink-0" />
                                            <div>
                                                <p className="text-gray-400 text-xs font-medium uppercase">Valor a pagar</p>
                                                <p className="text-gray-800 font-semibold">
                                                    {brl(valorCobradoFatura(fatura) ?? boleto?.valor_com_desconto ?? boleto?.valor_total ?? fatura.valor_fatura_distribuidora)}
                                                </p>
                                            </div>
                                        </div>
                                        <div className="flex items-start gap-2.5">
                                            <Zap size={17} className="text-primary mt-0.5 shrink-0" />
                                            <div>
                                                <p className="text-gray-400 text-xs font-medium uppercase">Consumo</p>
                                                <p className="text-gray-800 font-semibold">
                                                    {typeof fatura.consumo_ativo_kwh === 'number' ? `${fatura.consumo_ativo_kwh.toLocaleString('pt-BR')} kWh` : '—'}
                                                </p>
                                            </div>
                                        </div>
                                        <div className="flex items-start gap-2.5">
                                            <Leaf size={17} className="text-primary mt-0.5 shrink-0" />
                                            <div>
                                                <p className="text-gray-400 text-xs font-medium uppercase">Sua economia</p>
                                                <p className={`font-semibold ${economia ? 'text-green-600' : 'text-gray-800'}`}>
                                                    {economia ? brl(economia) : '—'}
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                    </div>

                                    {/* Dropdowns: "Entenda sua fatura" e visualizador de PDF */}
                                    <div className="flex border-t border-gray-100">
                                        <button
                                            onClick={() => alternarExpansao(fatura, 'detalhes', !!pdfFaturaUrl)}
                                            className={`flex-1 flex items-center justify-center gap-2 py-3 text-sm font-semibold transition-colors ${aberta && painel === 'detalhes' ? 'text-primary bg-primary/5' : 'text-primary hover:bg-primary/5'}`}
                                        >
                                            <Info size={16} />
                                            Entenda sua fatura
                                            <ChevronDown size={16} className={`transition-transform duration-200 ${aberta && painel === 'detalhes' ? 'rotate-180' : ''}`} />
                                        </button>
                                        {temAlgumPdf && (
                                            <button
                                                onClick={() => alternarExpansao(fatura, 'pdf', !!pdfFaturaUrl)}
                                                className={`flex-1 flex items-center justify-center gap-2 py-3 border-l border-gray-100 text-sm font-semibold transition-colors ${aberta && painel === 'pdf' ? 'text-primary bg-primary/5' : 'text-primary hover:bg-primary/5'}`}
                                            >
                                                <Eye size={16} />
                                                Visualizar no site
                                                <ChevronDown size={16} className={`transition-transform duration-200 ${aberta && painel === 'pdf' ? 'rotate-180' : ''}`} />
                                            </button>
                                        )}
                                    </div>

                                    {/* Painel "Entenda sua fatura" */}
                                    {aberta && painel === 'detalhes' && (
                                        <div className="border-t border-gray-100 bg-gray-50 p-4 sm:p-5 space-y-4">
                                            {(fatura.leitura_anterior || fatura.leitura_atual || fatura.proxima_leitura) && (
                                                <div>
                                                    <p className="text-xs font-bold text-gray-500 uppercase mb-2">Período de leitura</p>
                                                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-2.5">
                                                        {fatura.leitura_anterior && <ItemDetalhe rotulo="Leitura anterior" valor={dataBr(fatura.leitura_anterior)} />}
                                                        {fatura.leitura_atual && <ItemDetalhe rotulo="Leitura atual" valor={dataBr(fatura.leitura_atual)} />}
                                                        {fatura.proxima_leitura && <ItemDetalhe rotulo="Próxima leitura" valor={dataBr(fatura.proxima_leitura)} />}
                                                    </div>
                                                </div>
                                            )}

                                            <div>
                                                <p className="text-xs font-bold text-gray-500 uppercase mb-2">Energia</p>
                                                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2.5">
                                                    <ItemDetalhe
                                                        rotulo="Consumo do mês"
                                                        valor={typeof fatura.consumo_ativo_kwh === 'number' ? `${fatura.consumo_ativo_kwh.toLocaleString('pt-BR')} kWh` : '—'}
                                                    />
                                                    {((fatura.energia_injetada_gd1_kwh ?? 0) + (fatura.energia_injetada_gd2_kwh ?? 0)) > 0 && (
                                                        <ItemDetalhe
                                                            rotulo="Energia compensada"
                                                            valor={`${((fatura.energia_injetada_gd1_kwh ?? 0) + (fatura.energia_injetada_gd2_kwh ?? 0)).toLocaleString('pt-BR')} kWh`}
                                                            classeValor="text-primary"
                                                        />
                                                    )}
                                                </div>
                                            </div>

                                            <div>
                                                <p className="text-xs font-bold text-gray-500 uppercase mb-2">Composição do valor</p>
                                                {partes && (
                                                    <p className="text-xs text-gray-500 mb-2">
                                                        Boleto único: a conta da Equatorial vai junto e é paga automaticamente quando você paga o boleto. Não pague a Equatorial à parte.
                                                    </p>
                                                )}
                                                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2.5">
                                                    {partes ? (
                                                        <>
                                                            <ItemDetalhe rotulo="Conta da Equatorial" valor={brl(partes.equatorial)} />
                                                            <ItemDetalhe rotulo={marca.rotuloBoleto} valor={brl(partes.energia)} />
                                                            <ItemDetalhe rotulo="Total do boleto" valor={brl(valorCobradoFatura(fatura))} classeValor="text-primary" />
                                                        </>
                                                    ) : (
                                                        <>
                                                            {typeof fatura.valor_fatura_distribuidora === 'number' && (
                                                                <ItemDetalhe rotulo="Fatura da distribuidora" valor={brl(fatura.valor_fatura_distribuidora)} />
                                                            )}
                                                            {typeof fatura.valor_boleto_vigor === 'number' && (
                                                                <ItemDetalhe rotulo={marca.rotuloBoleto} valor={brl(fatura.valor_boleto_vigor)} />
                                                            )}
                                                        </>
                                                    )}
                                                    {typeof fatura.valor_adc_bandeira === 'number' && fatura.valor_adc_bandeira !== 0 && (
                                                        <ItemDetalhe rotulo="Adicional de bandeira" valor={brl(fatura.valor_adc_bandeira)} />
                                                    )}
                                                    {typeof fatura.ilum_publica === 'number' && fatura.ilum_publica !== 0 && (
                                                        <ItemDetalhe rotulo="Iluminação pública" valor={brl(fatura.ilum_publica)} />
                                                    )}
                                                </div>
                                            </div>

                                            {(typeof fatura.icms === 'number' || typeof fatura.pis === 'number' || typeof fatura.cofins === 'number') && (
                                                <div>
                                                    <p className="text-xs font-bold text-gray-500 uppercase mb-2">Alíquotas de impostos</p>
                                                    <div className="grid grid-cols-3 gap-2.5">
                                                        <ItemDetalhe rotulo="ICMS" valor={pct(fatura.icms)} />
                                                        <ItemDetalhe rotulo="PIS" valor={pct(fatura.pis)} />
                                                        <ItemDetalhe rotulo="COFINS" valor={pct(fatura.cofins)} />
                                                    </div>
                                                </div>
                                            )}

                                            {(typeof fatura.custo_s_vigor === 'number' && fatura.custo_s_vigor > 0) && (
                                                <div className="bg-primary/5 border border-primary/15 rounded-xl px-4 py-3.5 grid grid-cols-3 gap-3 text-center">
                                                    <div>
                                                        <p className="text-gray-400 text-[11px] font-medium uppercase">{marca.economia.comparativoSem}</p>
                                                        <p className="text-sm font-bold text-gray-500 line-through">{brl(fatura.custo_s_vigor)}</p>
                                                    </div>
                                                    <div>
                                                        <p className="text-gray-400 text-[11px] font-medium uppercase">{marca.economia.comparativoCom}</p>
                                                        <p className="text-sm font-bold text-gray-800">{brl(fatura.total_com_vigor ?? undefined)}</p>
                                                    </div>
                                                    <div>
                                                        <p className="text-gray-400 text-[11px] font-medium uppercase">Você economizou</p>
                                                        <p className="text-sm font-bold text-green-600">{economia ? brl(economia) : '—'}</p>
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    )}

                                    {/* Painel do visualizador de PDF */}
                                    {temAlgumPdf && (
                                        <>
                                            {aberta && painel === 'pdf' && urlVisualizada && (
                                                <div className="border-t border-gray-100 bg-gray-50 p-3 sm:p-4">
                                                    {pdfFaturaUrl && boletoUrl && (
                                                        <div className="flex bg-gray-200/70 rounded-lg p-1 mb-3 max-w-xs mx-auto">
                                                            {([['fatura', 'Fatura'], ['boleto', 'Boleto']] as const).map(([valor, rotulo]) => (
                                                                <button
                                                                    key={valor}
                                                                    onClick={() => setDocVisivel(valor)}
                                                                    className={`flex-1 py-1.5 rounded-md text-xs font-bold transition-all ${docVisivel === valor
                                                                        ? 'bg-white text-primary shadow-sm'
                                                                        : 'text-gray-500 hover:text-gray-700'
                                                                        }`}
                                                                >
                                                                    {rotulo}
                                                                </button>
                                                            ))}
                                                        </div>
                                                    )}
                                                    <iframe
                                                        src={urlVisualizada}
                                                        title={`Documento ${normalizeMesReferencia(fatura.mes_referencia)} — UC ${fatura.uc_codigo}`}
                                                        className="w-full h-[480px] sm:h-[640px] rounded-xl border border-gray-200 bg-white"
                                                    />
                                                    <p className="text-center text-xs text-gray-400 mt-2">
                                                        Se o documento não carregar aqui, use os botões "PDF" acima para abrir em nova aba.
                                                    </p>
                                                </div>
                                            )}
                                        </>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                )}
            </main>

            {faturaPagamento && (
                <PagamentoModal
                    fatura={faturaPagamento}
                    quitacao={boletosPorFatura.get(faturaPagamento.id)}
                    onClose={() => setFaturaPagamento(null)}
                />
            )}
        </div>
    );
};

export default PortalConsumidorFaturas;
